import React, { useState, useEffect, useCallback } from 'react';
import Web3 from 'web3';
import detectEthereumProvider from '@metamask/detect-provider';
import { useNavigate } from 'react-router-dom';
import '../style/admin.css'; // Ensure this includes your loader CSS
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
const contractDOC = require("./EmployeeDocumentManagement.json");
const DOCABI = contractDOC.abi;
const AdvancedEmployeeDocumentManagementADDRESS = process.env.REACT_APP_CONTRACT_ADDRESS;
const AdminVerifier = () => {
    const [employeeDataList, setEmployeeDataList] = useState([]);
    const [employes, setEmployees] = useState([]);
    const [verifiers, setVerifiers] = useState([]);
    const [verifierNames, setVerifierNames] = useState({});
    const [newEmployeeAddress, setNewEmployeeAddress] = useState('');
    const [newEmployeeName, setNewEmployeeName] = useState('');
    const [newEmployeeDepartment, setNewEmployeeDepartment] = useState('HR'); // Default to HR
    const [newEmployeeIdNumber, setNewEmployeeIdNumber] = useState('');
    const [newVerifierAddress, setNewVerifierAddress] = useState('');
    const [newVerifierName, setNewVerifierName] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [filterStatus, setFilterStatus] = useState('all');
    const navigate = useNavigate();

    useEffect(() => {
        const checkUserRole = async () => {
            const provider = await detectEthereumProvider();
            if (provider) {
                const web3 = new Web3(provider);
                const contract = new web3.eth.Contract(DOCABI, AdvancedEmployeeDocumentManagementADDRESS);

                try {
                    const accounts = await provider.request({ method: 'eth_requestAccounts' });
                    const walletAddress = accounts[0];
                    const role = 'admin'; // This should be dynamically determined if necessary
                    let roleConstant;
                    switch (role) {
                        case 'employee':
                            roleConstant = await contract.methods.EMPLOYEE_ROLE().call();
                            break;
                        case 'admin':
                            roleConstant = await contract.methods.ADMIN_ROLE().call();
                            break;
                        case 'verifier':
                            roleConstant = await contract.methods.APPROVER_ROLE().call();
                            break;
                        case 'owner':
                            roleConstant = null;
                            break;
                        default:
                            throw new Error("Unknown role");
                    }

                    if (roleConstant) {
                        const hasRole = await contract.methods.hasRole(roleConstant, walletAddress).call();
                        if (hasRole) {
                            navigate(`/${role}`, { state: { walletAddress } });
                        } else {
                            toast.error("You do not have the required role to access this page.");
                            navigate('/');
                        }
                    } else {
                        navigate('/owner', { state: { walletAddress } });
                    }
                } catch (error) {
                    console.error('Error during role check or navigation:', error);
                    toast.error("Check your role and try again.");
                    navigate('/');
                }
            } else {
                console.error('MetaMask is not installed.');
                toast.error('MetaMask is not installed. Please install MetaMask and try again.');
                navigate('/');
            }
        };

        checkUserRole();
    }, [navigate]);

    const fetchEmployeeData = useCallback(async () => {
        setLoading(true);
        const provider = await detectEthereumProvider();
        const web3 = new Web3(provider);
        const contract = new web3.eth.Contract(DOCABI, AdvancedEmployeeDocumentManagementADDRESS);
        try {
            const DATA = [];
            const getemployeeAddresses = await contract.methods.getAllEmployees().call();
            for (let i = 0; i < getemployeeAddresses.length; i++) {
                const ids = await contract.methods.getEmployeeDocuments(getemployeeAddresses[i]).call();
                const info = await contract.methods.getEmployeeInfo(getemployeeAddresses[i]).call();
                for (let index = 0; index < ids.length; index++) {
                    const employeeData = await contract.methods.getDocument(ids[index]).call();
                    const verifiers = await contract.methods.getDocument(ids[index]).call();

                    DATA.push({
                        employeeName: info.name,
                        employeeID: employeeData.id,
                        addedAt: employeeData[4],
                        documentType: employeeData.documentType,
                        documentHash: employeeData.documentHash,
                        verificationStatus: employeeData.isApproved,
                        verifiedBy: employeeData.verifiedBy,
                        approvedAt: employeeData.approvedAt,
                        document: `${process.env.REACT_APP_PINATA_URL}${employeeData.documentHash}`
                    });
                }
            }
            setEmployeeDataList(DATA);
        } catch (error) {
            console.error('Error fetching employee data:', error);
            toast.error('Error fetching employee data.');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchRoles = useCallback(async () => {
        setLoading(true);
        const provider = await detectEthereumProvider();
        const web3 = new Web3(provider);
        const contract = new web3.eth.Contract(DOCABI, AdvancedEmployeeDocumentManagementADDRESS);
        try {
            const allEmployeeAddresses = await contract.methods.getAllEmployees().call();
            const allVerifiers = await contract.methods.getAllApprovers().call();
            
            // Fetch employee names
            const employeesData = await Promise.all(allEmployeeAddresses.map(async address => {
                const info = await contract.methods.getEmployeeInfo(address).call();
                const name =  info.name;
                return { address, name };
            }));
            
            // Fetch verifier names
            const verifierNames = {};
            for (const address of allVerifiers) {
                const name = await contract.methods.getApproverName(address).call();
                verifierNames[address] = name;
            }
            
            setEmployees(employeesData);
            setVerifiers(allVerifiers);
            setVerifierNames(verifierNames); // Update verifier names state
        } catch (error) {
            console.error('Error fetching roles:', error);
            toast.error('Error fetching roles.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchEmployeeData();
        fetchRoles();
    }, [fetchEmployeeData, fetchRoles]);

    const handleDownload = async (ipfsHash, fileName) => {
        try {
            const ipfsUrl = `${process.env.REACT_APP_PINATA_URL}${ipfsHash}`;
            const response = await fetch(ipfsUrl);
            if (!response.ok) {
                throw new Error('Failed to fetch file from IPFS');
            }
            const blob = await response.blob();
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading file from IPFS:', error);
            toast.error('Error downloading file from IPFS.');
        }
    };

    const handleAddEmployee = async () => {
        const provider = await detectEthereumProvider();
        const web3 = new Web3(provider);
        const contract = new web3.eth.Contract(DOCABI, AdvancedEmployeeDocumentManagementADDRESS);
        const accounts = await web3.eth.getAccounts();
    
        // Map the year and branch to their corresponding enum indices
        const yearMapping = { HR: 0, Engineering: 1, Sales: 2, Marketing: 3 };//HR, Engineering, Sales, Marketing
        const branchMapping = { Mechanical: 0, Civil: 1, CS: 2, Mechatronics: 3 };
    
        const yearIndex = yearMapping[newEmployeeDepartment];
        // const branchIndex = branchMapping[newEmployeeBranch];
    
        try {
            await contract.methods.addEmployee(
                newEmployeeAddress,
                newEmployeeName,
                yearIndex,
                // branchIndex,
                newEmployeeIdNumber
            ).send({ from: accounts[0] });
            toast.success('Employee added successfully!');
            setNewEmployeeAddress('');
            setNewEmployeeName('');
            setNewEmployeeDepartment('HR'); // Reset to default value
            // setNewEmployeeBranch('Mechanical'); // Reset to default value
            setNewEmployeeIdNumber(''); // Clear ID number input
            fetchRoles();
        } catch (error) {
            console.error('Error adding employee:', error);
            toast.error('Failed to add employee.');
        }
    };
    

    const handleAddVerifier = async () => {
        const provider = await detectEthereumProvider();
        const web3 = new Web3(provider);
        const contract = new web3.eth.Contract(DOCABI, AdvancedEmployeeDocumentManagementADDRESS);
        const accounts = await web3.eth.getAccounts();
        try {
            await contract.methods.addApprover(newVerifierAddress, newVerifierName).send({ from: accounts[0] });
            console.log(newVerifierAddress, newVerifierName);
            toast.success('Verifier added successfully!');
            setNewVerifierAddress('');
            setNewVerifierName(''); // Clear the verifier's name field
            fetchRoles();
        } catch (error) {
            console.error('Error adding verifier:', error);
            toast.error('Failed to add verifier.');
        }
    };

    const handleRemoveVerifier = async (address) => {
        const provider = await detectEthereumProvider();
        const web3 = new Web3(provider);
        const contract = new web3.eth.Contract(DOCABI, AdvancedEmployeeDocumentManagementADDRESS);
        const accounts = await web3.eth.getAccounts();
        try {
            await contract.methods.removeApprover(address).send({ from: accounts[0] });
            toast.success('Verifier removed successfully!');
            fetchRoles();
        } catch (error) {
            console.error('Error removing verifier:', error);
            toast.error('Failed to remove verifier.');
        }
    };

    const handleRemoveEmployee = async (address) => {
        const provider = await detectEthereumProvider();
        const web3 = new Web3(provider);
        const contract = new web3.eth.Contract(DOCABI, AdvancedEmployeeDocumentManagementADDRESS);
        const accounts = await web3.eth.getAccounts();
        try {
            await contract.methods.removeEmployee(address).send({ from: accounts[0] });
            toast.success('Employee removed successfully!');
            fetchRoles();
        } catch (error) {
            console.error('Error removing employee:', error);
            toast.error('Failed to remove employee.');
        }
    };

    const handleRemoveDocument = async (documentId) => {
        const provider = await detectEthereumProvider();
        const web3 = new Web3(provider);
        const contract = new web3.eth.Contract(DOCABI, AdvancedEmployeeDocumentManagementADDRESS);
        const accounts = await web3.eth.getAccounts();
        try {
            await contract.methods.removeDocument(documentId).send({ from: accounts[0] });
            toast.success('Document removed successfully!');
            fetchEmployeeData();
        } catch (error) {
            console.error('Error removing document:', error);
            toast.error('Failed to remove document.');
        }
    };

    const renderCell = (content) => {
        return content || 'NA';
    };

    const getVerificationStatusStyle = (status) => {
        return {
            color: status ? 'green' : 'red',
            fontWeight: 'bold'
        };
    };

    function convertTimestampToDate(timestamp) {
        const date = new Date(timestamp * 1000);
        const options = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        };
        return date.toLocaleString('en-US', options);
    }

    const openModal = (documentUrl) => {
        setSelectedDocument(documentUrl);
    };

    const closeModal = () => {
        setSelectedDocument(null);
    };

    // Filter employeeDataList based on filterStatus
    const filteredEmployeeDataList = employeeDataList.filter(data => {
        if (filterStatus === 'all') return true;
        return filterStatus === 'verified' ? data.verificationStatus : !data.verificationStatus;
    });

    return (
        <div >
            <h1 style={{color:"#9b2620"}} >Admin Dashboard</h1>
            {loading ? (
                <div className="loader"></div>
            ) : (
                <>
                    <div className="filter-container">
                        <label htmlFor="filter-status">Filter by Status:</label>
                        <select
                            id="filter-status"
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                        >
                            <option value="all">All</option>
                            <option value="verified">Verified</option>
                            <option value="notVerified">Not Verified</option>
                        </select>
                    </div>

                    <div className="table-container">
                        <h2>Employee Documents</h2>
                        {filteredEmployeeDataList.length > 0 && (
                            <table>
                                <thead>
                                    <tr>
                                        <th>Employee Name</th>
                                        <th>Document ID</th>
                                        <th>Added At</th>
                                        <th>Document Type</th>
                                        <th>Document Hash</th>
                                        <th>Verification Status</th>
                                        <th>Verified By</th>
                                        <th>Verified At</th>
                                        <th>View Document</th>
                                        <th>Download</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredEmployeeDataList.map((data, index) => (
                                        <tr key={index}>
                                            <td>{renderCell(data.employeeName?.toString())}</td>
                                            <td>{renderCell(data.employeeID?.toString())}</td>
                                            <td>{renderCell(convertTimestampToDate(Number(data.addedAt).toString()))}</td>
                                            <td>{renderCell(data.documentType)}</td>
                                            <td>{renderCell(data.documentHash)}</td>
                                            <td style={getVerificationStatusStyle(data.verificationStatus)}>
                                                {data.verificationStatus ? 'Verified' : 'Not Verified'}
                                            </td>
                                            <td>{renderCell(data.verifiedBy?.join(', '))}</td>
                                            <td>{data.approvedAt ? convertTimestampToDate(data.approvedAt.toString()) : 'NA'}</td>
                                            <td>
                                                <button
                                                    onClick={() => openModal(data.document)}
                                                    className="view-document-button"
                                                >
                                                    View Document
                                                </button>
                                            </td>
                                            <td>
                                                <button
                                                    onClick={() => handleDownload(data.documentHash, `${data.employeeName}-${data.documentType}-${data.employeeID}`)}
                                                    className="download-button"
                                                >
                                                    Download
                                                </button>
                                            </td>
                                            <td>
                                                <button
                                                    onClick={() => handleRemoveDocument(data.employeeID)}
                                                    className="remove-button"
                                                >
                                                    Remove
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div className="admin-container">
                        <h2>Add Employee</h2>
                        <input
                            type="text"
                            value={newEmployeeAddress}
                            onChange={(e) => setNewEmployeeAddress(e.target.value)}
                            placeholder="Employee Address"
                        />
                        <input
                            type="text"
                            value={newEmployeeName}
                            onChange={(e) => setNewEmployeeName(e.target.value)}
                            placeholder="Employee Name"
                        />
                          <input
                            type="text"
                            value={newEmployeeIdNumber}
                            onChange={(e) => setNewEmployeeIdNumber(e.target.value)}
                            placeholder="ID Number"
                        />
                        <div className="filter-container">
                        <label htmlFor="year">Year:</label>
                        <select
                            id="year"
                            value={newEmployeeDepartment}
                            onChange={(e) => setNewEmployeeDepartment(e.target.value)}
                        >
                            <option value="HR">HR</option>
                            <option value="Engineering">Engineering</option>
                            <option value="Sales">Sales</option>
                            <option value="Marketing">Marketing</option>
                        </select>
                        {/* <label htmlFor="branch">Branch:</label>
                        <select
                            id="branch"
                            value={newEmployeeBranch}
                            onChange={(e) => setNewEmployeeBranch(e.target.value)}
                        >
                            <option value="Mechanical">Mechanical</option>
                            <option value="Civil">Civil</option>
                            <option value="CS">CS</option>
                            <option value="Mechatronics">Mechatronics</option>
                        </select> */}
                        </div>
                      
                        <button onClick={handleAddEmployee}>Add Employee</button>

                        <h2>Add Verifier</h2>
                       
                        <input
                            type="text"
                            value={newVerifierAddress}
                            onChange={(e) => setNewVerifierAddress(e.target.value)}
                            placeholder="Verifier Address"
                        />
                         <br></br>
                        <input
                            type="text"
                            value={newVerifierName}
                            onChange={(e) => setNewVerifierName(e.target.value)}
                            placeholder="Verifier Name"
                        />
                         <br></br>
                        <button onClick={handleAddVerifier}>Add Verifier</button>

                        <h2>Manage Roles</h2>
                        <div className="roles-container">
                            <h3>Employees</h3>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Address</th>
                                        <th>Name</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {employes.map((employee, index) => (
                                        <tr key={index}>
                                            <td>{employee.address}</td>
                                            <td>{employee.name}</td>
                                            <td>
                                                <button onClick={() => handleRemoveEmployee(employee.address)}>Remove</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <h3>Verifiers</h3>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Address</th>
                                        <th>Name</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {verifiers.map((verifier, index) => (
                                        <tr key={index}>
                                            <td>{verifier}</td>
                                            <td>{verifierNames[verifier] || 'NA'}</td>
                                            <td>
                                                <button onClick={() => handleRemoveVerifier(verifier)}>Remove</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
            <ToastContainer />
            {selectedDocument && (
                <div className="modal">
                    <div className="modal-content">
                        <span className="close" onClick={closeModal}>&times;</span>
                        <iframe
                            src={selectedDocument}
                            title="Document Viewer"
                            width="100%"
                            height="600px"
                        ></iframe>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminVerifier;
